import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Loader2, AlertTriangle, CheckCircle2, CreditCard, Phone, MessageSquare, Receipt } from "lucide-react";
import { api, BRAND } from "../lib/config";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

const APPROVAL_STYLES = {
  pending:       "bg-amber-100 text-amber-900 border-amber-300",
  approved:      "bg-sky-100 text-sky-900 border-sky-200",
  rejected:      "bg-rose-100 text-rose-900 border-rose-200",
  auto_approved: "bg-emerald-100 text-emerald-900 border-emerald-300",
  payment_pending:   "bg-orange-100 text-orange-900 border-orange-300",
};

const APPROVAL_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  auto_approved: "Auto Approved",
  payment_pending: "Payment Due",
};

export default function LongGrassApprovalCard({ booking, onUpdated }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [working, setWorking] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const lg = booking?.long_grass_fee;

  // Refresh the countdown every 30s while a pending timer is showing.
  useEffect(() => {
    const approval = lg?.customer_approval || "pending";
    if (approval !== "pending" || !lg?.auto_approve_at) return;
    const t = setInterval(() => setNowTs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [lg?.customer_approval, lg?.auto_approve_at]);

  // If we just came back from Stripe with lg_paid=1, finalize the payment
  useEffect(() => {
    if (!lg || lg.paid_at) return;
    const lgPaid = params.get("lg_paid");
    const sessionId = params.get("session_id");
    const focusBooking = params.get("booking");
    if (lgPaid === "1" && sessionId && focusBooking === booking.booking_id) {
      (async () => {
        try {
          const { data } = await api.post(
            `/bookings/${booking.booking_id}/long-grass-fee/finalize-payment?session_id=${encodeURIComponent(sessionId)}`
          );
          toast.success(`Long grass fee paid — $${(lg.amount || 0).toFixed(2)}`);
          onUpdated?.();
          if (data.tx_id) setTimeout(() => navigate(`/receipt/${data.tx_id}`), 1500);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not finalize payment");
        }
      })();
    }
    if (lgPaid === "0") toast.message("Payment cancelled — long grass fee still due.");
  }, [params, booking?.booking_id, lg, onUpdated, navigate]);

  if (!lg || !lg.amount) return null;

  const approval = lg.customer_approval || "pending";
  const isPaid = !!lg.paid_at;
  const isRejected = approval === "rejected";
  const isPaymentDue = approval === "payment_pending";
  const isAutoApproved = approval === "auto_approved";

  // Countdown to the 5h auto-charge deadline (only meaningful while pending).
  const autoAt = lg.auto_approve_at ? new Date(lg.auto_approve_at).getTime() : null;
  const secondsRemaining = autoAt ? Math.max(0, Math.floor((autoAt - nowTs) / 1000)) : 0;
  const hoursRemaining = Math.floor(secondsRemaining / 3600);
  const minutesRemaining = Math.floor((secondsRemaining % 3600) / 60);
  const countdownLabel = autoAt && approval === "pending" && lg.eligible_for_auto
    ? (secondsRemaining > 0 ? `Auto-charges in ${hoursRemaining}h ${minutesRemaining}m` : "Auto-charge processing…")
    : null;

  const approve = async () => {
    setWorking("approve");
    try {
      // Try immediate off-session charge when eligible (card + ToS).
      const { data } = await api.post(
        `/bookings/${booking.booking_id}/long-grass-fee/respond`,
        { approved: true, charge_now: true }
      );
      if (data?.charged) {
        toast.success(`Approved & paid — $${(lg.amount || 0).toFixed(2)}`);
      } else {
        toast.success("Approved — tap Pay Now to complete.");
      }
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve");
    } finally { setWorking(null); }
  };

  const reject = async () => {
    if (!window.confirm("Reject this long grass fee? Our team will reach out to discuss.")) return;
    setWorking("reject");
    try {
      await api.post(`/bookings/${booking.booking_id}/long-grass-fee/respond`, { approved: false });
      toast.success("Fee rejected — we'll get in touch.");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject");
    } finally { setWorking(null); }
  };

  const payNow = async () => {
    setWorking("pay");
    try {
      const { data } = await api.post(
        `/bookings/${booking.booking_id}/long-grass-fee/checkout`,
        { return_url: window.location.origin }
      );
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setWorking(null);
    }
  };

  if (isPaid) {
    return (
      <div className="rounded-2xl bg-emerald-50 border-2 border-emerald-200 p-4 mt-3" data-testid={`lg-paid-${booking.booking_id}`}>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-700" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-extrabold text-emerald-900">
              Long grass fee {isAutoApproved ? "auto-charged" : "paid"} · ${lg.amount.toFixed(2)}
            </div>
            <div className="text-[11px] text-emerald-800/80">
              {lg.height_label} · {lg.area_label} · {new Date(lg.paid_at).toLocaleDateString()}
              {isAutoApproved && " · via card on file"}
            </div>
          </div>
          {lg.tx_id && (
            <Button onClick={() => navigate(`/receipt/${lg.tx_id}`)} size="sm" variant="outline" className="border-emerald-300 text-emerald-800" data-testid="lg-paid-receipt">
              <Receipt className="w-3.5 h-3.5 mr-1" /> Receipt
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100/40 p-4 mt-3"
      data-testid={`lg-approval-${booking.booking_id}`}
    >
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-amber-200 text-amber-900 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-amber-800">Action required</div>
          <div className="text-base font-extrabold tracking-tight font-[Outfit] text-amber-950">Overgrown grass surcharge</div>
          <div className="text-[12px] text-amber-900/80">{lg.height_label} · {lg.area_label}</div>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.14em] font-extrabold border ${APPROVAL_STYLES[approval] || APPROVAL_STYLES.pending}`} data-testid={`lg-approval-status-${booking.booking_id}`}>
          {APPROVAL_LABEL[approval] || approval}
        </span>
      </div>

      {countdownLabel && (
        <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-extrabold text-amber-900 bg-amber-200/70 rounded-full px-2 py-0.5" data-testid="lg-auto-countdown">
          ⏱ {countdownLabel}
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Cell label="Original" value={`$${(lg.original_price || 0).toFixed(2)}`} />
        <Cell label="LG fee" value={`+$${lg.amount.toFixed(2)}`} highlight />
        <Cell label="Total due" value={`$${(lg.total_due || (lg.original_price + lg.amount)).toFixed(2)}`} />
      </div>

      {isRejected ? (
        <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 p-3 text-[12px]" data-testid="lg-rejected-note">
          You rejected this fee — our team will reach out to discuss. You can change your mind by approving below.
          <div className="mt-2">
            <Button onClick={approve} size="sm" className="rounded-full bg-amber-700 hover:bg-amber-800 text-white font-extrabold" data-testid="lg-reopen-approve">
              Reconsider & approve
            </Button>
          </div>
        </div>
      ) : isPaymentDue ? (
        <div className="mt-4 rounded-xl bg-orange-50 border border-orange-200 text-orange-900 p-3 text-[12px]" data-testid="lg-payment-due-note">
          The 5-hour approval window passed without a saved card + terms on file. This surcharge is now <b>Payment Due</b>.
          Tap below to pay now, or contact us if there&apos;s an issue.
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <Button
              onClick={payNow}
              disabled={!!working}
              data-testid="lg-payment-due-pay"
              className="flex-1 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold py-4"
            >
              {working === "pay" ? <Loader2 className="w-4 h-4 animate-spin" /> :
                <><CreditCard className="w-4 h-4 mr-1.5" /> Pay Now</>}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Button
            onClick={lg.eligible_for_auto ? approve : payNow}
            disabled={!!working}
            data-testid="lg-pay-now"
            className="flex-1 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold py-5 shadow ring-2 ring-amber-200"
          >
            {working === "pay" || working === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1.5" /> Approve & Pay Now</>}
          </Button>
          <a
            href={`tel:${BRAND.phoneTel}`}
            data-testid="lg-contact-call"
            className="flex-1 inline-flex items-center justify-center rounded-full border-2 border-amber-700 text-amber-900 font-extrabold py-3 hover:bg-amber-100"
          >
            <Phone className="w-4 h-4 mr-1.5" /> Contact JDSN
          </a>
        </div>
      )}

      {approval === "pending" && !isRejected && (
        <div className="mt-3 flex items-center justify-between gap-2 text-[11px]">
          <button
            type="button"
            onClick={approve}
            disabled={!!working}
            data-testid="lg-approve-only"
            className="text-amber-900 font-bold underline disabled:opacity-50"
          >
            Approve without paying yet
          </button>
          <button
            type="button"
            onClick={reject}
            disabled={!!working}
            data-testid="lg-reject"
            className="text-rose-700 font-bold underline disabled:opacity-50"
          >
            Reject fee
          </button>
        </div>
      )}

      <a
        href={`sms:${BRAND.phoneTel}`}
        data-testid="lg-contact-sms"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold text-amber-900 underline"
      >
        <MessageSquare className="w-3 h-3" /> Text us about this charge
      </a>
    </div>
  );
}

function Cell({ label, value, highlight }) {
  return (
    <div className={`rounded-xl px-3 py-2 ${highlight ? "bg-amber-200 text-amber-950" : "bg-white border border-amber-200 text-amber-950"}`}>
      <div className="text-[9px] uppercase tracking-[0.14em] font-extrabold text-amber-900/80">{label}</div>
      <div className="font-extrabold tabular-nums">{value}</div>
    </div>
  );
}
